// src/components/EventCard.js
import React from 'react';
import { COLORS } from '../theme'; 
import { 
  getMemberName, getMemberRole, getMemberPic, getYouTubeId, formatCurrency, getUserRank, DEFAULT_RANKS,
  hasPriorityRank, isRankAllowed 
} from '../utils'; 
import liff from '@line/liff';

const EventCard = ({ 
    event, lineUser, userInfo, appConfig, 
    onEventAction, onCheckIn, onCancelCheckIn, 
    onPayPay, onPointPayment, onCashPayment, onShare, 
    onChatPost, onChatDelete, openChatId, setOpenChatId, chatInput, setChatInput,
    activeChatMessages = [], // ★ サブコレクションのチャットデータ（未渡しでも落ちないよう既定値を置く）
    onMemberClick 
}) => {
    const isCancelled = event.status === "cancelled";
    const members = event.members || [];
    const waitlist = event.waitlist || [];
    
    const userName = userInfo?.displayName || lineUser?.displayName;

    const isJoined = lineUser && members.some(m => (m.userId === lineUser.userId) || getMemberName(m) === userName);
    const isWaiting = lineUser && waitlist.some(m => (m.userId === lineUser.userId) || getMemberName(m) === userName);
    
    const limitFP = event.limitFP !== undefined ? event.limitFP : 20;
    const limitGK = event.limitGK !== undefined ? event.limitGK : 2;

    const fpCount = members.filter(m => getMemberRole(m) === 'FP').length;
    const gkCount = members.filter(m => getMemberRole(m) === 'GK').length;
    
    const isOpenChat = openChatId === event.id;
    const hasResult = event.homeScore !== undefined && event.homeScore !== '';
    
    // ★変更: chatCount があればそれを表示。なければ過去の配列 (event.chat.length) の数をフォールバックとして表示
    const chatDisplayCount = event.chatCount !== undefined ? event.chatCount : (event.chat?.length || 0);

    // ★ サブコレクション化する前のメッセージは event.chat 配列に残っている。
    //   これを拾わないと、件数は出ているのに開くと空、という状態になる。
    //   旧メッセージは id を持たないので削除はできない（表示のみ）。
    const legacyChat = (event.chat || []).map(m => ({ ...m, id: null, _legacy: true }));
    // ★ 配列でない値（undefined 等）が渡ってもスプレッドで落ちないようにする。
    //   ここは描画中に評価されるため、例外が出ると画面全体が白くなる。
    const safeActiveChat = Array.isArray(activeChatMessages) ? activeChatMessages : [];
    const chatMessagesToDisplay = isOpenChat
        ? [...legacyChat, ...safeActiveChat]
        : [];

    const myInfo = isJoined ? members.find(m => (m.userId === lineUser.userId) || getMemberName(m) === userName) : null;
    const isCheckedIn = myInfo && myInfo.checkedInAt;
    const isTeamMode = (event.checkInType || 'team') === 'team';
    const youtubeId = getYouTubeId(event.youtubeUrl);

    const userRank = userInfo ? getUserRank(userInfo, appConfig.ranks || DEFAULT_RANKS) : DEFAULT_RANKS[DEFAULT_RANKS.length-1];
    
    // ★変更: ここで独自に myRankIds を組み立てるのをやめ、utils.js の
    //   getUserRankIds() に一本化した。
    //   旧実装は membership を monthly / collaborator としか比較しておらず、
    //   カスタムランク（rank_xxxxx）が membership にしか無いユーザーを取りこぼしていた。
    //   また rankExpiries（有効期限）も見ていなかった。
    //   userInfo が null のときは getUserRankIds() が ['visitor'] を返す。
    //
    // ★変更: allowedRanks の解釈も utils.js の isRankAllowed() に集約。
    const isRestricted = !isRankAllowed(userInfo, event.allowedRanks);

    // ★変更: priority の判定も utils.js の hasPriorityRank() に集約。
    const hasPriority = hasPriorityRank(userInfo, appConfig.ranks || DEFAULT_RANKS);
    const isVisitorRestricted = !hasPriority && event.visitorOpenDate && new Date() < new Date(event.visitorOpenDate);

    const baseFee = Number(event.fee) || 0;   
    let finalFee = baseFee;
    let isFeeFreeDisplay = false;
    let feeDisplayHtml = null;

    const pointUnit = appConfig.pointUnitLabel || 'RB';
    const hasPointFee = event.feePoint !== undefined && event.feePoint !== null && event.feePoint !== '';
    const basePointFee = hasPointFee ? (Number(event.feePoint) || 0) : baseFee;
    let finalPointFee = basePointFee;

    const isDiscountDisabled = event.isOfficialEvent === false;

    if (!isDiscountDisabled && userRank.type === 'free') {
        finalFee = 0;
        finalPointFee = 0;
        isFeeFreeDisplay = true;
        feeDisplayHtml = <span style={{color:COLORS.success}}>会員無料 ({userRank.name})</span>;
    } else if (!isDiscountDisabled && userRank.type === 'half') {
        finalFee = Math.floor(baseFee / 2);
        finalPointFee = Math.floor(basePointFee / 2);
        feeDisplayHtml = (
            <span>
                <span style={{textDecoration:'line-through', color:'#999', fontSize:'10px', marginRight:'5px'}}>¥{formatCurrency(baseFee)}</span>
                <span style={{color:'#e65100', fontWeight:'bold'}}>¥{formatCurrency(finalFee)}</span>
                <span style={{fontSize:'11px', marginLeft:'5px', color:'#666'}}>({formatCurrency(finalPointFee)}{pointUnit})</span>
            </span>
        );
    } else {
        feeDisplayHtml = (
            <span>
                ¥{formatCurrency(finalFee)} 
                <span style={{fontSize:'11px', color:'#666', marginLeft:'5px'}}>({formatCurrency(finalPointFee)}{pointUnit})</span>
                {isDiscountDisabled && <span style={{fontSize:'9px', color:'#d32f2f', marginLeft:'5px', fontWeight:'bold'}}>[特別イベント/割引対象外]</span>}
            </span>
        );
    }

    let isPastEventTime = false;
    try {
        if (event.isoDate && event.time && event.time.includes('-')) {
            const now = new Date();
            const [year, month, day] = event.isoDate.split('-').map(Number);
            const [startTimeStr] = event.time.split('-'); 
            const [startHour, startMin] = startTimeStr.split(':').map(Number);
            
            const eventStartTime = new Date(year, month - 1, day, startHour, startMin);
            
            if (now >= eventStartTime) {
                isPastEventTime = true;
            }
        }
    } catch (e) {
        console.error("Event time check error:", e);
    }

    const isPaidInDB = event.paidMembers && userName && event.paidMembers.includes(userName);
    const isPaid = isPastEventTime ? isPaidInDB : (isFeeFreeDisplay || isPaidInDB);
    const canCheckIn = isPaid;

    const waitFP_ = waitlist.filter(m => getMemberRole(m) === 'FP').length;
    const waitGK_ = waitlist.filter(m => getMemberRole(m) === 'GK').length;
    const remainFP_ = limitFP - fpCount;
    const remainGK_ = limitGK - gkCount;
    const fpStatus = waitFP_ > 0 ? `FP待ち(${waitFP_})` : `FP残${remainFP_ > 0 ? remainFP_ : 0}`;
    const gkStatus = waitGK_ > 0 ? `GK待ち(${waitGK_})` : `GK残${remainGK_ > 0 ? remainGK_ : 0}`;
    const statusText = `${fpStatus} / ${gkStatus}`;

    let statusBadge = null;
    if (!isCancelled) {
        if (isRestricted) {
            statusBadge = <span className="badge" style={{background:'#eee', color:'#999'}}>🚫 参加対象外</span>;
        } else if (isVisitorRestricted) {
            statusBadge = <span className="badge" style={{background:'#fff8e1', color:'#f57f17', border:'1px solid #ffe0b2'}}>🔒 先行受付中</span>;
        } else {
            let bg = '#e8f5e9', col = '#2e7d32';
            if (waitlist.length > 0) { bg = COLORS.danger; col = 'white'; } 
            else if (remainFP_ <= 0 && remainGK_ <= 0) { bg = '#666'; col = 'white'; } 
            statusBadge = <span className="badge" style={{background:bg, color:col}}>{statusText}</span>;
        }
    }

    const whiteTeam = members.filter(m => m.team === 'white').sort((a,b) => (a.number||0) - (b.number||0));
    const blackTeam = members.filter(m => m.team === 'black').sort((a,b) => (a.number||0) - (b.number||0));

    const headerBg = isCancelled 
        ? '#ef5350' 
        : (isDiscountDisabled ? 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' : 'linear-gradient(to right, #eceff1, #ffffff)');
    const headerBorder = isCancelled 
        ? '2px solid #ef5350' 
        : (isDiscountDisabled ? '2px solid #ffe082' : '2px solid #cfd8dc');

    return (
        <div className="card" style={{ 
            padding: '0', 
            overflow: 'hidden', 
            opacity: isCancelled ? 0.8 : 1,
            border: '1px solid #cfd8dc', 
            boxShadow: '0 6px 15px rgba(0,0,0,0.1)', 
            marginBottom: '25px', 
            borderRadius: '12px'
        }}>
            <div style={{background: headerBg, padding:'15px', borderBottom: headerBorder}}>
                <div className="event-header">
                    <div className="event-header-info">
                        <h2 style={{ margin: 0, fontSize: '18px', color: isCancelled ? 'white' : '#333', lineHeight:'1.4', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {isCancelled && <span>【中止】</span>} 
                            {!isCancelled && !isDiscountDisabled && <span style={{background: COLORS.primary, color: 'white', fontSize: '11px', padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap', boxShadow:'0 1px 2px rgba(0,0,0,0.1)'}}>👑 公式</span>}
                            {!isCancelled && isDiscountDisabled && <span style={{background: '#ffb300', color: 'white', fontSize: '11px', padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap', boxShadow:'0 1px 2px rgba(0,0,0,0.1)'}}>🌟 特別</span>}
                            <span>{event.title}</span>
                        </h2>
                        
                        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <div style={{ 
                                display: 'inline-flex', alignItems: 'center', 
                                background: isCancelled ? 'rgba(255,255,255,0.2)' : (isDiscountDisabled ? 'white' : '#fff3e0'), 
                                color: isCancelled ? 'white' : '#e65100', 
                                padding: '4px 10px', borderRadius: '6px', 
                                fontWeight: '900', fontSize: '15px', letterSpacing: '1px',
                                border: isCancelled ? '1px solid rgba(255,255,255,0.5)' : (isDiscountDisabled ? '1px solid #ffe0b2' : '1px solid #ffe0b2'),
                                boxShadow: isCancelled ? 'none' : '0 2px 4px rgba(230,81,0,0.15)'
                            }}>
                                🕘 {event.time || '未定'}
                            </div>
                        </div>

                        <div className="text-truncate" style={{fontSize:'12px', color: isCancelled ? 'rgba(255,255,255,0.9)' : '#666'}}>
                            📍 {event.mapUrl ? <a href={event.mapUrl} target="_blank" rel="noopener noreferrer" style={{color: isCancelled ? 'white' : COLORS.primary, textDecoration:'underline'}}>{event.venue}</a> : event.venue}
                        </div>
                    </div>
                    <div className="event-badge-container">
                        {statusBadge}
                    </div>
                </div>
            </div>
            
            <div style={{ padding: '15px' }}>
                {!isCancelled && (
                    <div style={{textAlign:'right', marginBottom:'10px'}}>
                        <a href={`https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.isoDate.replace(/-/g, '')}T${(event.time?event.time.split('-')[0].replace(':',''):'0000')}00/${event.isoDate.replace(/-/g, '')}T${(event.time?event.time.split('-')[1].replace(':',''):'0000')}00&details=${encodeURIComponent('VS '+(event.opponentName||''))}&location=${encodeURIComponent(event.venue||'')}`} target="_blank" rel="noopener noreferrer" className="badge" style={{background: '#e3f2fd', color: COLORS.primary, textDecoration:'none'}}>📅 カレンダー登録</a>
                    </div>
                )}
                
                {hasResult ? (
                    <div style={{backgroundColor:'#2d3748', color:'white', borderRadius:'12px', padding:'20px', marginBottom:'20px', textAlign:'center', boxShadow:'inset 0 2px 4px rgba(0,0,0,0.5)'}}>
                            <div style={{fontSize:'10px', color:'#a0aec0', marginBottom:'10px', letterSpacing:'1px'}}>FULL TIME</div>
                            <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:'15px'}}>
                                <div style={{flex:1, textAlign:'right', display:'flex', justifyContent:'flex-end', alignItems:'center'}}>
                                    {appConfig.teamEmblemUrl && <img src={appConfig.teamEmblemUrl} style={{width:'50px', height:'50px', objectFit:'contain'}} alt="Emblem" />}
                                </div>
                                <div style={{background:'rgba(0,0,0,0.5)', padding:'5px 15px', borderRadius:'8px', fontSize:'28px', fontWeight:'bold', color:COLORS.secondary}}>{event.homeScore} - {event.awayScore}</div>
                                <div style={{flex:1, textAlign:'left', fontWeight:'bold', fontSize:'14px'}}>{event.opponentName || '相手'}</div>
                            </div>
                            {event.mom && <div style={{marginTop:'10px', color:COLORS.secondary, fontWeight:'bold', fontSize:'12px'}}>🏅 MOM: {event.mom}</div>}
                            
                            {event.matchDetails && event.matchDetails.length > 0 && (
                                <div style={{marginTop:'15px', borderTop:'1px dashed #4a5568', paddingTop:'10px', textAlign:'left'}}>
                                    <div style={{color:'#a0aec0', marginBottom:'8px', textAlign:'center', fontSize:'10px', letterSpacing:'1px'}}>SCORERS</div>
                                    <div style={{display:'flex', flexDirection:'column', width:'100%', padding:'0 5px', boxSizing:'border-box'}}>
                                        {[...event.matchDetails].sort((a,b) => Number(a.minute) - Number(b.minute)).map((d, i) => (
                                            <div key={i} className="score-detail-row">
                                                <div className="score-detail-minute">{d.minute}'</div>
                                                <div className="score-detail-content">
                                                    <span style={{fontWeight:'bold', color:'white'}}>⚽ {d.scorer}</span>
                                                    {d.assist && d.assist !== 'なし' && d.assist !== '' && (
                                                        <span style={{color:'#cfd8dc', fontSize:'11px', marginLeft:'6px'}}>(👟 {d.assist})</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {youtubeId && (
                                <div style={{marginTop:'15px', borderTop:'1px dashed #4a5568', paddingTop:'10px'}}>
                                    <a href={event.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{display:'block', position:'relative', borderRadius:'8px', overflow:'hidden'}}>
                                        <img src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`} style={{width:'100%', display:'block'}} alt="thumbnail" />
                                        <div style={{position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'40px', height:'40px', background:'rgba(255,0,0,0.8)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:'20px'}}>▶</div>
                                    </a>
                                </div>
                            )}
                            <div style={{marginTop:'15px', textAlign:'center'}}>
                                <button onClick={()=>onShare(event)} className="btn btn-sm" style={{background:'rgba(255,255,255,0.1)', color:'white'}}>📤 結果をシェア</button>
                            </div>
                    </div>
                ) : (
                    <>
                    <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '12px', marginBottom: '15px', fontSize:'13px', borderLeft:`4px solid ${COLORS.secondary}` }}>
                            {event.opponentName && <div style={{fontWeight:'bold', marginBottom:'5px'}}>⚔️ VS {event.opponentName}</div>}
                            {(event.myUniformFP || event.myUniformGK) && (
                                <div style={{fontSize:'12px', color:'#555', marginBottom:'2px'}}>👕 自: {event.myUniformFP && `FP ${event.myUniformFP}`} {event.myUniformGK && `/ GK ${event.myUniformGK}`}</div>
                            )}
                            {(event.opponentUniformFP || event.opponentUniformGK) && (
                                <div style={{fontSize:'12px', color:'#555', marginBottom:'5px'}}>👕 敵: {event.opponentUniformFP && `FP ${event.opponentUniformFP}`} {event.opponentUniformGK && `/ GK ${event.opponentUniformGK}`}</div>
                            )}
                            <div style={{color:'#333', marginTop:'5px', fontWeight:'bold'}}>参加費: {feeDisplayHtml}</div>
                    </div>
                    
                    {event.description && (
                        <div style={{marginBottom:'15px', padding:'10px', background:'#fff3e0', border:'1px solid #ffe0b2', borderRadius:'8px', fontSize:'12px', color:'#e65100', whiteSpace:'pre-wrap'}}>
                            <div style={{fontWeight:'bold', marginBottom:'5px'}}>⚠️ 注意事項</div>
                            {event.description}
                        </div>
                    )}

                    {event.belongings && event.belongings.length > 0 && (
                        <div style={{marginBottom:'15px', padding:'10px', background:'#e1f5fe', border:'1px solid #b3e5fc', borderRadius:'8px', fontSize:'12px', color:'#0277bd'}}>
                            <div style={{fontWeight:'bold', marginBottom:'5px'}}>🎒 持ち物</div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                                {event.belongings.map((item, i) => (
                                    <span key={i} style={{background:'white', padding:'3px 8px', borderRadius:'10px', border:'1px solid #81d4fa'}}>{item}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isCancelled && (
                            <div style={{marginBottom:'20px'}}>
                                {!lineUser ? <button onClick={()=>liff.login({ redirectUri: window.location.href })} className="btn btn-success btn-full">LINEログインして参加</button>
                                : (
                                    <>
                                        {isJoined ? (
                                            <div style={{marginBottom:'10px'}}>
                                                {isCheckedIn ? (
                                                    <>
                                                        {isTeamMode ? (
                                                            <div style={{background: myInfo.team==='white'?'white':'#333', color: myInfo.team==='white'?'#333':'white', border:'2px solid #333', borderRadius:'12px', padding:'15px', textAlign:'center', boxShadow:'0 4px 10px rgba(0,0,0,0.1)'}}>
                                                                <div style={{fontSize:'12px', opacity:0.8}}>今日のポジション</div>
                                                                <div style={{fontSize:'28px', fontWeight:'bold', letterSpacing:'2px'}}>{myInfo.team==='white'?'⚪ 白':'⚫ 黒'} {getMemberRole(myInfo) === 'GK' ? 'GK' : `#${myInfo.number}`}</div>
                                                            </div>
                                                        ) : (
                                                            <div style={{textAlign:'center', padding:'10px', background:'#e8f5e9', color:COLORS.success, borderRadius:'8px', fontWeight:'bold'}}>✅ チェックイン完了</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <button 
                                                        onClick={() => onCheckIn(event)} 
                                                        className="btn btn-full" 
                                                        style={{
                                                            background: canCheckIn ? `linear-gradient(45deg, ${COLORS.primary}, #21CBF3)` : '#ff9800', 
                                                            color: 'white',
                                                            marginBottom:'10px',
                                                            boxShadow: canCheckIn ? '0 4px 10px rgba(33,150,243,0.3)' : '0 4px 10px rgba(255,152,0,0.3)'
                                                        }}
                                                    >
                                                        {canCheckIn ? '📍 到着したらチェックイン！' : '⚠️ お支払い後にチェックイン'}
                                                    </button>
                                                )}

                                                {!isFeeFreeDisplay && (
                                                    <div className="payment-section">
                                                        <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'8px', color:'#666'}}>参加費の支払い</div>
                                                        <div className="payment-grid">
                                                            <button 
                                                                onClick={() => onPayPay({...event, fee: finalFee})} 
                                                                disabled={isPaidInDB || !event.paypayLink} 
                                                                className={`btn btn-payment ${isPaidInDB ? 'disabled' : ''}`} 
                                                                style={{background: isPaidInDB ? '#ccc' : '#FF0033', color:'white'}}
                                                            >
                                                                <span className="btn-icon-large">📱</span>
                                                                <div style={{lineHeight:1}}>
                                                                    <div style={{fontSize:'10px', opacity:0.8}}>PayPay送金</div>
                                                                    <div style={{fontSize:'14px', fontWeight:'bold'}}>{isPaidInDB ? '支払済' : 'アプリ起動'}</div>
                                                                </div>
                                                            </button>

                                                            <button 
                                                                onClick={() => onPointPayment({...event, fee: finalFee, feePoint: finalPointFee})} 
                                                                disabled={isPaidInDB} 
                                                                className={`btn btn-payment ${isPaidInDB ? 'disabled' : ''}`} 
                                                                style={{background: isPaidInDB ? '#ccc' : '#e65100', color:'white'}}
                                                            >
                                                                <span className="btn-icon-large">
                                                                    <img src="/rbcoin.png" alt="coin" style={{width:'20px', height:'20px', objectFit:'contain'}} />
                                                                </span>
                                                                <div style={{lineHeight:1}}>
                                                                    <div style={{fontSize:'10px', opacity:0.8}}>コイン決済</div>
                                                                    <div style={{fontSize:'12px', fontWeight:'bold'}}>{isPaidInDB ? '支払済' : `残高: ${formatCurrency(userInfo?.points||0)}${pointUnit}`}</div>
                                                                </div>
                                                            </button>

                                                            <button 
                                                                onClick={() => onCashPayment({...event, fee: finalFee})} 
                                                                disabled={isPaidInDB} 
                                                                className={`btn btn-payment ${isPaidInDB ? 'disabled' : ''}`} 
                                                                style={{background: isPaidInDB ? '#ccc' : COLORS.secondary, color: isPaidInDB?'white':'#333'}}
                                                            >
                                                                <span className="btn-icon-large">💴</span>
                                                                <div style={{lineHeight:1}}>
                                                                    <div style={{fontSize:'10px', opacity:0.8}}>直接手渡し</div>
                                                                    <div style={{fontSize:'14px', fontWeight:'bold'}}>{isPaidInDB ? '支払済' : '当日現金'}</div>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {isPastEventTime ? (
                                                    <button disabled className="btn btn-full" style={{marginTop:'10px', background:'#ccc', color:'white', cursor:'not-allowed'}}>
                                                        開始時刻を過ぎたためキャンセル不可
                                                    </button>
                                                ) : (
                                                    <button onClick={()=>onEventAction(event)} className="btn btn-danger btn-full" style={{marginTop:'10px'}}>
                                                        キャンセル
                                                    </button>
                                                )}
                                            </div>
                                        ) : isWaiting ? (
                                            <div style={{marginBottom:'10px'}}>
                                                <div style={{textAlign:'center', marginBottom:'5px', color:'#e65100', fontWeight:'bold', fontSize:'12px'}}>キャンセル待ち中</div>
                                                {isPastEventTime ? (
                                                    <button disabled className="btn btn-full" style={{background:'#ccc', color:'white', cursor:'not-allowed'}}>
                                                        受付終了
                                                    </button>
                                                ) : (
                                                    <button onClick={()=>onEventAction(event)} className="btn btn-danger btn-full">
                                                        キャンセル待ちをやめる
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{display:'flex', gap:'10px'}}>
                                                <button 
                                                    onClick={()=>onEventAction(event,'FP')} 
                                                    disabled={isVisitorRestricted || isRestricted || isPastEventTime} 
                                                    className="btn btn-full" 
                                                    style={{background: (isVisitorRestricted||isRestricted||isPastEventTime)?'#ccc':(fpCount>=limitFP?COLORS.secondary:COLORS.success), color:'white', cursor: (isVisitorRestricted||isRestricted||isPastEventTime)?'not-allowed':'pointer'}}
                                                >
                                                    {isRestricted ? '対象外' : (isVisitorRestricted ? '受付前' : (isPastEventTime ? '受付終了' : (fpCount>=limitFP ? 'FP待機':'FP参加')))}
                                                </button>
                                                <button 
                                                    onClick={()=>onEventAction(event,'GK')} 
                                                    disabled={isVisitorRestricted || isRestricted || isPastEventTime} 
                                                    className="btn btn-full" 
                                                    style={{background: (isVisitorRestricted||isRestricted||isPastEventTime)?'#ccc':(gkCount>=limitGK?COLORS.secondary:COLORS.primary), color:'white', cursor: (isVisitorRestricted||isRestricted||isPastEventTime)?'not-allowed':'pointer'}}
                                                >
                                                    {isRestricted ? '対象外' : (isVisitorRestricted ? '受付前' : (isPastEventTime ? '受付終了' : (gkCount>=limitGK ? 'GK待機':'GK参加')))}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                    )}
                    </>
                )}

                {isCheckedIn && isTeamMode && (whiteTeam.length > 0 || blackTeam.length > 0) && (
                    <div style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                            <div style={{flex:1, background:'white', border:'1px solid #ddd', borderRadius:'8px', padding:'10px'}}>
                                <div style={{textAlign:'center', fontWeight:'bold', borderBottom:'1px solid #eee', marginBottom:'5px', color:'#333'}}>⚪ 白</div>
                                {whiteTeam.filter(m => getMemberRole(m) === 'FP').map(m => <div key={m.userId || `${getMemberName(m)}-${m.number}`} style={{fontSize:'12px', borderBottom:'1px dashed #eee'}}>{m.number}. {getMemberName(m)}</div>)}
                                {whiteTeam.filter(m => getMemberRole(m) === 'GK').length > 0 && (
                                    <>
                                        <div style={{textAlign:'center', fontSize:'10px', background:'#f5f5f5', border:'1px solid #ddd', padding:'2px', marginTop:'10px', marginBottom:'5px', borderRadius:'4px', color:'#666', fontWeight:'bold'}}>🧤 GK</div>
                                        {whiteTeam.filter(m => getMemberRole(m) === 'GK').map(m => <div key={m.userId || `${getMemberName(m)}-${m.number}`} style={{fontSize:'12px', borderBottom:'1px dashed #eee', color:COLORS.primary, fontWeight:'bold', textAlign:'center'}}>{getMemberName(m)}</div>)}
                                    </>
                                )}
                            </div>

                            <div style={{flex:1, background:'#333', color:'white', borderRadius:'8px', padding:'10px'}}>
                                <div style={{textAlign:'center', fontWeight:'bold', borderBottom:'1px solid #555', marginBottom:'5px'}}>⚫ 黒</div>
                                {blackTeam.filter(m => getMemberRole(m) === 'FP').map(m => <div key={m.userId || `${getMemberName(m)}-${m.number}`} style={{fontSize:'12px', borderBottom:'1px dashed #555'}}>{m.number}. {getMemberName(m)}</div>)}
                                {blackTeam.filter(m => getMemberRole(m) === 'GK').length > 0 && (
                                    <>
                                        <div style={{textAlign:'center', fontSize:'10px', background:'#4a4a4a', border:'1px solid #555', padding:'2px', marginTop:'10px', marginBottom:'5px', borderRadius:'4px', color:'#ccc', fontWeight:'bold'}}>🧤 GK</div>
                                        {blackTeam.filter(m => getMemberRole(m) === 'GK').map(m => <div key={m.userId || `${getMemberName(m)}-${m.number}`} style={{fontSize:'12px', borderBottom:'1px dashed #555', color:COLORS.primary, fontWeight:'bold', textAlign:'center'}}>{getMemberName(m)}</div>)}
                                    </>
                                )}
                            </div>
                    </div>
                )}

                <div style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '12px' }}>
                    <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'10px', color:'#555'}}>✅ 参加メンバー</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', gap: '8px' }}>
                            {members.map((m, i) => (
                                <div 
                                    key={i} 
                                    onClick={() => { if (m.userId && onMemberClick) onMemberClick(m.userId); }}
                                    style={{ 
                                        display:'flex', flexDirection:'column', alignItems:'center',
                                        cursor: m.userId ? 'pointer' : 'default',
                                        transform: 'scale(1)', transition: 'transform 0.2s'
                                    }}
                                    onMouseEnter={(e) => { if(m.userId) e.currentTarget.style.transform = 'scale(1.1)'; }}
                                    onMouseLeave={(e) => { if(m.userId) e.currentTarget.style.transform = 'scale(1)'; }}
                                >
                                    <div style={{ position:'relative', width:'40px', height:'40px' }}>
                                        {getMemberPic(m) ? <img src={getMemberPic(m)} alt="" style={{width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover'}} /> : <div style={{width:'100%', height:'100%', borderRadius:'50%', background:'#ddd', display:'flex', alignItems:'center', justifyContent:'center'}}>👤</div>}
                                        {getMemberRole(m) === 'GK' && <div style={{position:'absolute', top:-2, left:-2, fontSize:'12px'}}>🧤</div>}
                                        {m.checkedInAt && <div style={{position:'absolute', bottom:0, right:0, width:'10px', height:'10px', background:COLORS.success, borderRadius:'50%', border:'2px solid white'}}></div>}
                                    </div>
                                    <div style={{fontSize:'9px', marginTop:'2px', width:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'center'}}>{getMemberName(m)}</div>
                                </div>
                            ))}
                    </div>
                    {members.length === 0 && <div style={{fontSize:'12px', color:'#ccc', textAlign:'center'}}>参加者なし</div>}
                </div>

                {waitlist.length > 0 && (
                    <div style={{ marginTop:'15px', backgroundColor: '#fff3e0', padding: '10px', borderRadius: '12px', border:`1px dashed ${COLORS.secondary}` }}>
                            <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'5px', color:'#e65100'}}>⌛ キャンセル待ち ({waitlist.length}人待ち)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', gap: '8px' }}>
                                {waitlist.map((m, i) => (
                                    <div 
                                        key={i} 
                                        onClick={() => { if (m.userId && onMemberClick) onMemberClick(m.userId); }}
                                        style={{ 
                                            display:'flex', flexDirection:'column', alignItems:'center', opacity: 0.8,
                                            cursor: m.userId ? 'pointer' : 'default',
                                            transform: 'scale(1)', transition: 'transform 0.2s'
                                        }}
                                        onMouseEnter={(e) => { if(m.userId) e.currentTarget.style.transform = 'scale(1.1)'; }}
                                        onMouseLeave={(e) => { if(m.userId) e.currentTarget.style.transform = 'scale(1)'; }}
                                    >
                                        <div style={{ position:'relative', width:'40px', height:'40px' }}>
                                            <div style={{position:'absolute', top:-5, right:-5, width:'16px', height:'16px', background:'#e65100', color:'white', borderRadius:'50%', fontSize:'10px', fontWeight:'bold', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2, border:'1px solid white'}}>{i + 1}</div>
                                            {getMemberPic(m) ? <img src={getMemberPic(m)} alt="" style={{width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover'}} /> : <div style={{width:'100%', height:'100%', borderRadius:'50%', background:'#ddd', display:'flex', alignItems:'center', justifyContent:'center'}}>👤</div>}
                                        </div>
                                        <div style={{fontSize:'9px', marginTop:'2px', width:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'center'}}>{getMemberName(m)}</div>
                                    </div>
                                ))}
                            </div>
                    </div>
                )}
                
                <div style={{marginTop:'15px'}}>
                    <button onClick={() => setOpenChatId(isOpenChat ? null : event.id)} style={{background:'none', border:'none', color:COLORS.primary, fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px'}}>
                            💬 連絡ボード <span style={{background:'#eee', padding:'2px 6px', borderRadius:'10px', fontSize:'10px'}}>{chatDisplayCount}</span>
                    </button>
                    {isOpenChat && (
                            <div style={{background:'#f8f9fa', padding:'10px', borderRadius:'12px', marginTop:'5px', border:'1px solid #eee'}}>
                                <div style={{maxHeight:'200px', overflowY:'auto', marginBottom:'10px'}}>
                                {/* ★ 変更: chatMessagesToDisplay を map で回す */}
                                {chatMessagesToDisplay.map((msg, i) => {
                                    // ★ ルールは userId で削除権限を判定するので、表示側も userId で揃える。
                                    //   名前比較のままだと、表示名を変えた人に「×」が出るのに
                                    //   押すと権限エラーになる。旧メッセージは userId が無いため名前で代用する。
                                    const myId = userInfo?.userId || lineUser?.userId;
                                    const isMine = msg.userId
                                        ? (!!myId && msg.userId === myId)
                                        : (!!userName && msg.name === userName);
                                    const isAdmin = userInfo && (userInfo.role === 'staff' || userInfo.role === 'admin');
                                    return (
                                        <div key={msg.id || i} style={{fontSize:'12px', marginBottom:'8px', display:'flex', alignItems:'flex-start', gap:'8px', flexDirection: isMine?'row-reverse':'row'}}>
                                            {msg.pic ? <img src={msg.pic} style={{width:'24px', height:'24px', borderRadius:'50%'}} alt="" /> : <div style={{width:'24px', height:'24px', borderRadius:'50%', background:'#ccc'}}></div>}
                                            <div style={{background: isMine?'#e3f2fd':'white', padding:'8px', borderRadius:'12px', maxWidth:'70%', boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
                                                <div style={{fontWeight:'bold', fontSize:'10px', marginBottom:'2px', color:'#666'}}>{msg.name}</div>
                                                {msg.text}
                                            </div>
                                            {/* ★ 変更: 削除時にドキュメントID (msg.id) を渡す */}
                                            {/* ★ 旧メッセージ（idなし）は削除できないのでボタンを出さない */}
                                            {msg.id && (isMine || isAdmin) && <span onClick={()=>onChatDelete(event, msg.id)} style={{cursor:'pointer', fontSize:'10px', color:'#999'}}>×</span>}
                                        </div>
                                    );
                                })}
                                </div>
                                <div style={{display:'flex', gap:'5px'}}><input value={chatInput} onChange={e=>setChatInput(e.target.value)} style={{flex:1, fontSize:'12px', margin:0, padding:'8px'}} placeholder="メッセージ..." /><button onClick={()=>onChatPost(event)} className="btn btn-primary btn-sm">送信</button></div>
                            </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventCard;